import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get or create session ID
function getSessionId() {
  let sid = sessionStorage.getItem('samtwo_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('samtwo_sid', sid);
  }
  return sid;
}

const trackEvent = (event, page, data = {}) => {
  axios.post(`${API}/analytics/event`, {
    event, page, data, session_id: getSessionId()
  }).catch(() => {});
};

// Hook for button click tracking
export const useTrackClick = (buttonName, page) => {
  return useCallback(() => {
    trackEvent('button_click', page || window.location.pathname, { button: buttonName });
  }, [buttonName, page]);
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const enterTime = useRef(Date.now());
  const lastPath = useRef('');

  useEffect(() => {
    // Track time spent on previous page
    if (lastPath.current && lastPath.current !== location.pathname) {
      const seconds = Math.round((Date.now() - enterTime.current) / 1000);
      if (seconds > 1) {
        trackEvent('time_spent', lastPath.current, { seconds });
      }
    }

    // Track new page view
    lastPath.current = location.pathname;
    enterTime.current = Date.now();
    trackEvent('page_view', location.pathname);

    // Track time on page unload
    const handleUnload = () => {
      const seconds = Math.round((Date.now() - enterTime.current) / 1000);
      if (seconds > 1) {
        // Use sendBeacon for reliability on page unload
        const data = JSON.stringify({
          event: 'time_spent',
          page: location.pathname,
          data: { seconds },
          session_id: getSessionId()
        });
        navigator.sendBeacon?.(`${API}/analytics/event`, new Blob([data], { type: 'application/json' }));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [location.pathname]);

  return null;
};

export { trackEvent };
export default AnalyticsTracker;
